import { ActionTree } from 'vuex';
import { LockService } from '@/services/LockService';
import {
  CabinetModel,
  DocumentToStoreModel,
  DocumentToStoreSearchModel,
  DocumentToStoreViewModel,
  ErrorModel,
  ExistingDocumentOption,
  LockModel,
  RootState,
  SchemaModel,
  DocumentToStoreState,
  DocumentToStoreMutationTypes,
  KeyValuePair,
  Dictionary,
  TargetDocumentTypeSettingsModel,
  MultiValueConstants,
  MultiValueReferenceModel
} from '@/types';
import { DocumentToStoreService } from '@/services';

export const actions: ActionTree<DocumentToStoreState, RootState> = {
  setDocumentToStoreView(this: any, { commit, getters }, payLoad: DocumentToStoreViewModel) {
    commit(DocumentToStoreMutationTypes.SetDocumentToStoreView, payLoad);

    const initialSchema = [...payLoad.dynamicFormSchema];
    const initialDocumentDate = getters.documentDateReferenceValue;
    commit(DocumentToStoreMutationTypes.SetInitialSchemaValues, { initialSchema, initialDocumentDate });
    commit(DocumentToStoreMutationTypes.SetInitialMultiValueReferences, Array.isArray(payLoad.multiValueReferences) ? [...payLoad.multiValueReferences] : []);
  },
  setDynamicFormSchema(this: any, { commit }, payLoad: SchemaModel[]) {
    commit(DocumentToStoreMutationTypes.SetDynamicFormSchema, payLoad);
  },
  setMultiValueReferenceValues(this: any, { commit }, payLoad: any[]) {
    commit(DocumentToStoreMutationTypes.SetMultiValueReferenceValues, payLoad);
  },
  clearMultiValueReferenceValues(this: any, { commit }) {
    commit(DocumentToStoreMutationTypes.SetMultiValueReferenceValues, []);
  },
  setMultiValueReferencesFromExistingDocument(this: any, { commit, state }, multiValueReferences: MultiValueReferenceModel[]) {
    if (!multiValueReferences?.length) {
      commit(DocumentToStoreMutationTypes.SetMultiValueReferenceValues, []);
      return;
    }
    const multiValueSchema = state.documentToStoreView.multiValueDynamicFormSchema;
    const multiValueReferenceValues = multiValueReferences.map((ref, idx) => {
      const refValues = ref.values || {};
      const refMap = Object.fromEntries(Object.entries(refValues).map(([k, v]) => [k.toLowerCase(), v]));
      const item = multiValueSchema.reduce(
        (acc: { [key: string]: any }, schemaField: SchemaModel) => {
          const fieldId = schemaField.id.toLowerCase();
          acc[fieldId] = refMap[fieldId] ?? '';
          return acc;
        },
        { [MultiValueConstants.RowIdAlias]: idx + 1 }
      );
      return item;
    });
    commit(DocumentToStoreMutationTypes.SetMultiValueReferenceValues, multiValueReferenceValues);
  },

  setIsLoading({ commit }, isLoading: boolean) {
    commit(DocumentToStoreMutationTypes.SetIsLoading, isLoading);
  },
  setIsDirty({ commit, dispatch }, isDirty: boolean) {
    dispatch('applicationDirtyStore/setApplicationDirty', isDirty, { root: true });
    commit(DocumentToStoreMutationTypes.SetIsDirty, isDirty);
  },
  setReadonly({ commit }, isReadonly: boolean) {
    commit(DocumentToStoreMutationTypes.SetIsReadonly, isReadonly);
  },
  getDocumentToStore({ commit }, payload: DocumentToStoreSearchModel) {
    commit(DocumentToStoreMutationTypes.SetIsLoading, true);
    commit(DocumentToStoreMutationTypes.SetExistingDocumentId, 0);
    commit(DocumentToStoreMutationTypes.SetExistingDocumentOption, ExistingDocumentOption.AddToDocument);
    commit(DocumentToStoreMutationTypes.SetPageViewedBeforeAddToExisting, 1);

    const initialDocumentToStoreView = {
      cabinets: [] as CabinetModel[],
      dynamicFormSchema: [] as SchemaModel[],
      targetDocumentTypeSettings: {} as TargetDocumentTypeSettingsModel
    } as DocumentToStoreViewModel;

    commit(DocumentToStoreMutationTypes.SetDocumentToStoreView, initialDocumentToStoreView);

    DocumentToStoreService.getDocumentToStoreAsync(payload.sourceCabinetId, payload.sourceDocumentId)
      .then((response: DocumentToStoreModel) => {
        commit(DocumentToStoreMutationTypes.DocumentToStoreLoaded, response);
      })
      .catch((error: any) => {
        commit(DocumentToStoreMutationTypes.DocumentToStoreError, error);
      });
  },
  extendLock({ commit, getters }) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      commit(DocumentToStoreMutationTypes.SetExtendingDocumentLock, true);
      LockService.extendLockAsync(currentLock.id)
        .then((response: LockModel) => {
          const payload: LockModel = response;
          commit(DocumentToStoreMutationTypes.ExtendDocumentLock, payload);
          commit(DocumentToStoreMutationTypes.SetExtendingDocumentLock, false);
        })
        .catch((error: any) => {
          commit(DocumentToStoreMutationTypes.ExpireDocumentLock, error);
          commit(DocumentToStoreMutationTypes.SetExtendingDocumentLock, false);
        });
    }
  },
  releaseDocumentToStoreLock({ commit, getters }) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      LockService.releaseLockAsync(currentLock.id).then(() => {
        commit(DocumentToStoreMutationTypes.ExpireDocumentLock);
      });
    }
  },
  setExtendingLock({ commit }, payload: boolean) {
    commit(DocumentToStoreMutationTypes.SetExtendingDocumentLock, payload);
  },
  setComponentName({ commit }, componentName: string) {
    commit(DocumentToStoreMutationTypes.SetComponentName, componentName);
  },
  setErrorModel({ commit }, errorModel: ErrorModel) {
    commit(DocumentToStoreMutationTypes.SetErrorModel, errorModel);
  },
  setSchemaValuesFromReferences({ commit }, references: Dictionary<string>) {
    const modelKeys = Object.getOwnPropertyNames(references);
    modelKeys.forEach((k: string) => {
      const reference = { key: k, value: references[k] } as KeyValuePair<string>;
      commit(DocumentToStoreMutationTypes.SetSchemaValueFromReference, reference);
    });
  },
  setSchemaValuesFromRetainedFields({ commit, state }, fields: SchemaModel[]) {
    const setFieldValue = (field: SchemaModel) => {
      const reference = { key: field.id, value: field.value } as KeyValuePair<string>;
      commit(DocumentToStoreMutationTypes.SetSchemaValueFromReference, reference);
    };
    const documentReferenceMatches = (field: SchemaModel): boolean => {
      const referenceIndex = state.documentToStoreView.dynamicFormSchema.findIndex(
        (schema: SchemaModel) => schema.id.toLowerCase() === field.id.toLowerCase() && schema.label.toLowerCase() === field.label.toLowerCase()
      );
      return referenceIndex !== -1;
    };

    const fieldsByType = (typePrefix: string): SchemaModel[] => {
      return fields.filter((field: SchemaModel) => field.id.toLowerCase().startsWith(typePrefix));
    };
    if (state.documentToStoreView.targetDocumentTypeSettings.retainCommonReferenceValues) {
      fieldsByType('cref').forEach((commonField: SchemaModel) => {
        setFieldValue(commonField);
      });
    }
    if (state.documentToStoreView.targetDocumentTypeSettings.retainDocumentReferenceValues) {
      fieldsByType('dref').forEach((documentField: SchemaModel) => {
        if (documentReferenceMatches(documentField)) {
          setFieldValue(documentField);
        }
      });
    }
  },
  setDocumentDateValue({ commit }, documentDate: Date | null) {
    commit(DocumentToStoreMutationTypes.SetDocumentDateValue, documentDate);
  },
  setSourceDocumentTypeId({ commit }, documentTypeId: number) {
    commit(DocumentToStoreMutationTypes.SetSourceDocumentTypeId, documentTypeId);
  },
  selectExistingDocument({ commit }, pageViewedBeforeAddToExisting: number) {
    commit(DocumentToStoreMutationTypes.SetPageViewedBeforeAddToExisting, pageViewedBeforeAddToExisting);
    commit(DocumentToStoreMutationTypes.SetComponentName, 'SelectExistingDocument');
  },
  setExistingDocumentId({ commit }, documentId: number) {
    commit(DocumentToStoreMutationTypes.SetExistingDocumentId, documentId);
  },
  setExistingDocumentOption({ commit }, option: ExistingDocumentOption) {
    commit(DocumentToStoreMutationTypes.SetExistingDocumentOption, option);
  },
  clearExistingDocument({ commit }) {
    commit(DocumentToStoreMutationTypes.SetExistingDocumentId, 0);
    commit(DocumentToStoreMutationTypes.SetExistingDocumentOption, ExistingDocumentOption.AddToDocument);
  },
  setKeepPagesAfterStoring({ state, commit }, payload: boolean) {
    commit(DocumentToStoreMutationTypes.SetKeepPagesAfterStoring, payload);
  },
  setSelectedPages({ state, commit }, payload: string) {
    commit(DocumentToStoreMutationTypes.SetSelectedPages, payload);
  },
  setDocumentStatus({ state, commit }, payLoad: string) {
    commit(DocumentToStoreMutationTypes.SetDocumentStatus, payLoad);
  },
  setAllPagesSelectedWithManualPageSelection({ state, commit }, payload: boolean) {
    commit(DocumentToStoreMutationTypes.SetAllPagesSelectedWithManualPageSelection, payload);
  },
  setAllPagesSelectedWithBatchSplitting({ state, commit }, payload: boolean) {
    commit(DocumentToStoreMutationTypes.SetAllPagesSelectedWithBatchSplitting, payload);
  },
  setReferencesForNextDocument({ state, commit }, fields: SchemaModel[]) {
    commit(DocumentToStoreMutationTypes.SetReferencesForNextDocument, fields);
  },
  setMultiValueReferencesForNextDocument({ state, commit }, fields: []) {
    commit(DocumentToStoreMutationTypes.SetMultiValueReferencesForNextDocument, fields);
  },
  setApplyReferencesToNextDocumentEnabled({ state, commit }, enabled: boolean) {
    commit(DocumentToStoreMutationTypes.ApplyReferencesToNextDocumentEnabled, enabled);
  },
  clearReferencesForNextDocument({ state, commit }) {
    commit(DocumentToStoreMutationTypes.ClearReferencesForNextDocument);
  },
  applyReferencesToNextDocument({ state, commit }) {
    const setFieldValue = (field: SchemaModel) => commit(DocumentToStoreMutationTypes.SetSchemaValueFromReference, { key: field.id, value: field.value });

    const refs = state.referencesForNextDocument || [];
    const schema = state.documentToStoreView.dynamicFormSchema || [];
    refs.forEach((field) => {
      const id = field.id?.toLowerCase();
      if (id?.startsWith('cref')) {
        setFieldValue(field);
      } else if (id?.startsWith('dref')) {
        if (schema.some((s) => s.id?.toLowerCase() === id && s.label?.toLowerCase() === field.label?.toLowerCase())) {
          setFieldValue(field);
        }
      }
    });

    const multiValueSchema = state.documentToStoreView.multiValueDynamicFormSchema;
    const multiValueRefs = state.multiValueReferencesForNextDocument;
    if (!multiValueSchema?.length || !multiValueRefs?.length) {
      return;
    }

    const multiValueReferenceValues = multiValueRefs.map((ref, idx) => {
      const refMap = Object.fromEntries(Object.entries(ref).map(([k, v]) => [k.toLowerCase(), v]));
      const item = multiValueSchema.reduce(
        (acc: { [key: string]: any }, schemaField) => {
          const fieldId = schemaField.id.toLowerCase();
          acc[fieldId] = refMap[fieldId];
          return acc;
        },
        { [MultiValueConstants.RowIdAlias]: idx + 1 }
      );
      return item;
    });
    commit(DocumentToStoreMutationTypes.SetMultiValueReferenceValues, multiValueReferenceValues);
  }
};
